/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CallbackProfilesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneIncoming, Edit, Trash2, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { useCallbackProfileList } from './hooks';
import { deleteCallbackProfileById, navigateToCallbackProfileDuplicate } from './callbackProfilesActions';
import type { CallbackProfileResponse } from './types';

export default function CallbackProfilesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; profile: CallbackProfileResponse | null }>({
    isOpen: false,
    profile: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useCallbackProfileList();

  // Add render functions to columns (JSX must be in .tsx file)
  const columns = useMemo(() => {
    return baseColumns.map((column) => {
      if (column.key === 'name') {
        return {
          ...column,
          render: (_value: unknown, item: CallbackProfileResponse) => (
            <div className="flex items-center gap-2">
              <PhoneIncoming className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-foreground">{item?.name ?? ''}</span>
            </div>
          )
        };
      }

      if (column.key === 'description') {
        return {
          ...column,
          render: (_: unknown, item: CallbackProfileResponse) => (
            <span className="text-sm text-muted-foreground">
              {item?.description || '�'}
            </span>
          )
        };
      }

      if (column.key === 'enabled') {
        return {
          ...column,
          render: (_value: unknown, item: CallbackProfileResponse) => (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                item?.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
            >
              {item?.enabled ? t('common.enabled') : t('common.disabled')}
            </span>
          )
        };
      }

      if (column.key === 'updatedAt') {
        return {
          ...column,
          render: (_value: unknown, item: CallbackProfileResponse) => (
            <span className="text-sm text-muted-foreground">
              {item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : '�'}
            </span>
          )
        };
      }

      if (column.key === 'actions') {
        return {
          ...column,
          render: (_value: unknown, item: CallbackProfileResponse) => renderRowActions(item)
        };
      }

      return column;
    });
  }, [baseColumns, t]);

  const handleCreate = useCallback(() => {
    navigate('/pbx/call-center/callback-profiles/new');
  }, [navigate]);

  const handleEdit = useCallback((item: CallbackProfileResponse) => {
    navigate(`/pbx/call-center/callback-profiles/${item.uuid || item.id}`);
  }, [navigate]);

  const handleDuplicate = useCallback((item: CallbackProfileResponse) => {
    navigateToCallbackProfileDuplicate(navigate, String(item.uuid || item.id));
  }, [navigate]);

  const handleDeleteClick = useCallback((item: CallbackProfileResponse) => {
    setDeleteModal({ isOpen: true, profile: item });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.profile) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteCallbackProfileById(String(deleteModal.profile.uuid || deleteModal.profile.id));

      showSuccess(t('callbackProfiles.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, profile: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('callbackProfiles.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.profile, refresh, showError, showSuccess, t]);

  const renderRowActions = useMemo(() => (item: CallbackProfileResponse) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(item)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(item)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onDuplicate={handleDuplicate}
          onColumnToggle={handleColumnToggle}
          title={t('callbackProfiles.title')}
          description={t('callbackProfiles.description')}
          titleIcon={<PhoneIncoming className="h-5 w-5 text-primary" />}
          addButtonText={t('callbackProfiles.actions.add')}
          renderActions={renderRowActions}
          emptyMessage={t('callbackProfiles.messages.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, profile: null })}
        onConfirm={handleDeleteConfirm}
        title={t('common.deleteConfirmation')}
        message={t('callbackProfiles.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
